/** @format */
import React, { useEffect, useMemo, useState } from 'react'
import { _$, Tooltip, Icon, Message, Dialog, Table, TableColumn, Drawer } from '@king-design/react'
import { MonitorSearch, TablePage } from 'src/components'
import { getRulesUnits } from 'src/containers/MonitorHistoryTactics/constants'
import { CollapseItemProps } from './constants'
import { handleExportForm } from 'src/utils'
import dayjs from 'dayjs'
const AlarmingCollapseItem: React.FC<CollapseItemProps> = ({
  visible,
  resources,
  regionGroup,
  productName,
  productType,
  projectList
}) => {
  // 表格筛选
  const [search, setSearch] = useState<string>('')
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  const [tableGroup, setTableGroup] = useState<{ Region: string }>({ Region: '' })
  const [page, setPage] = useState({
    pageIndex: 1,
    pageSize: 10
  })
  const { pageIndex, pageSize } = page
  // 云主机-关联硬盘弹窗
  const [realtedShow, setRealtedShow] = useState<boolean>(false)
  const [realtedData, setRelatedData] = useState<any>([])
  // 告警规则抽屉
  const [alarmShow, setAlarmShow] = useState<boolean>(false)
  const [alarmData, setAlarmData] = useState<any>([])

  useEffect(() => {
    if (visible) {
      setSearch('')
      setSelectedKeys([])
      setTableGroup({ Region: '' })
      setPage({
        pageIndex: 1,
        pageSize: 10
      })
    } else {
      setRealtedShow(false)
      setRelatedData([])
      setAlarmShow(false)
      setAlarmData([])
    }
  }, [visible])

  const scheme = useMemo(() => {
    const schemeConfig = {
      InstanceName: {
        title: _$('实例名称'),
        width: productType === 0 ? 200 : 250,
        template({ InstanceName, InstanceId }) {
          return (
            <div>
              <Tooltip content={InstanceName}>
                <span className="text-overflow">{InstanceName}</span>
              </Tooltip>
              <p style={{ color: '#999' }}>{InstanceId}</p>
            </div>
          )
        }
      },
      InstanceIP: {
        title: _$('实例信息'),
        width: productType === 0 ? 100 : 120
      },
      Region: {
        title: _$('Region'),
        width: productType === 0 ? 100 : 150,
        group: regionGroup,
        template(item) {
          return item.RegionName
        }
      },
      ProjectName: {
        title: _$('所属项目'),
        width: 100,
        hidden: productType !== 0
      },
      InstanceTag: {
        title: _$('标签'),
        width: 150,
        hidden: productType !== 0,
        template(data) {
          const { InstanceTags } = data
          return InstanceTags?.length
            ? InstanceTags?.map(text => {
                return <p>{text}</p>
              })
            : '-'
        }
      },
      Policies: {
        title: _$('告警规则'),
        width: 80,
        template(data) {
          return (
            <a
              onClick={() => {
                setAlarmShow(true)
                setAlarmData(data)
              }}
            >
              {data?.Policies?.length}
            </a>
          )
        }
      },
      RelatedResourceSet: {
        title: _$('关联硬盘'),
        width: 80,
        hidden: productType !== 0,
        template(data) {
          return (
            <a
              onClick={() => {
                setRealtedShow(true)
                setRelatedData(data)
              }}
            >
              {data.RelatedResourceSet?.length}
            </a>
          )
        }
      }
    }
    return schemeConfig
  }, [regionGroup, productType, projectList])

  const mapTableData = useMemo(() => {
    if (resources?.length) {
      const res = resources.map((cur: any) => {
        const {
          InstanceName,
          InstanceId,
          InstanceIP,
          Region,
          ProjectID,
          InstanceTags,
          Policies,
          RelatedResourceSet
        } = cur
        const findProject = projectList.find(({ ProjectId: id }) => id === ProjectID)
        const findRegion = regionGroup.find(({ value }) => Region === value)
        const map = Policies.map(item => {
          const { PolicyId, PolicyRule, PolicyName, CurValue, TriggerTime, Duration } = item
          const { units, triggerValue } = getRulesUnits(PolicyRule, CurValue, productType)
          return {
            PolicyName,
            PolicyId,
            PolicyRule: PolicyRule.replace(/LessThanLastPeriod/g, _$('周期环比下降')).replace(
              /GreaterThanLastPeriod/g,
              _$('周期环比上涨')
            ),
            CurValue: `${triggerValue}${units}`,
            TriggerTime,
            Duration
          }
        })
        return {
          InstanceName,
          InstanceId,
          InstanceIP,
          Region,
          RegionName: findRegion?.label || Region,
          ProjectID,
          ProjectName: findProject?.ProjectName || '-',
          InstanceTags: InstanceTags?.map((item: any) => `${item.Key} | ${item.Value}`) || [],
          RelatedResourceSet: RelatedResourceSet?.map((item: any) => item.InstanceID) || [],
          Policies: map.sort((a, b) => {
            if (a.TriggerTime && b.TriggerTime) {
              const aDate = dayjs(a.TriggerTime).unix()
              const bDate = dayjs(b.TriggerTime).unix()
              return bDate - aDate
            }
            return -1
          })
        }
      })
      return res
    } else {
      return []
    }
  }, [resources, projectList, regionGroup, productType])

  // 筛选表格数据
  const filterData = useMemo(() => {
    if (mapTableData?.length) {
      const filter = mapTableData.filter((item: any) => {
        const { InstanceName, InstanceId, Region } = item
        if (search && !InstanceName.includes(search) && !InstanceId.includes(search)) {
          return false
        }
        if (tableGroup.Region && Region !== tableGroup.Region) {
          return false
        }
        return true
      })
      return filter
    } else {
      return []
    }
  }, [mapTableData, search, tableGroup])

  // 导出数据
  const exportData = () => {
    const headers = [
      {
        key: 'instanceName',
        value: _$('实例名称')
      },
      {
        key: 'instanceId',
        value: _$('实例ID')
      },
      {
        key: 'instanceIP',
        value: _$('实例信息')
      },
      {
        key: 'Region',
        value: _$('Region')
      },
      {
        key: 'projectId',
        value: _$('所属项目')
      },
      {
        key: 'instanceTag',
        value: _$('标签')
      },
      {
        key: 'PolicyName',
        value: _$('告警策略名称')
      },
      {
        key: 'PolicyRule',
        value: _$('告警规则')
      },
      {
        key: 'CurValue',
        value: _$('触发值')
      },
      {
        key: 'TriggerTime',
        value: _$('触发时间')
      },
      {
        key: 'Duration',
        value: _$('持续时间')
      },
      {
        key: 'relatedResourceSet',
        value: _$('关联硬盘ID')
      }
    ]
    const filterKeyData = filterData?.filter(item => {
      if (!selectedKeys?.length || selectedKeys.includes(item.InstanceId)) {
        return true
      } else {
        return false
      }
    })
    const mapdata = filterKeyData.reduce((acc: any, cur: any) => {
      const {
        InstanceName,
        InstanceId,
        InstanceIP,
        RegionName,
        InstanceTags,
        ProjectName,
        Policies,
        RelatedResourceSet
      } = cur
      acc.push(
        ...Policies.map((item: any) => {
          const { PolicyName, PolicyRule, CurValue, TriggerTime, Duration } = item
          return [
            InstanceName,
            InstanceId,
            InstanceIP,
            RegionName,
            ProjectName || '-',
            InstanceTags.join('；') || '-',
            PolicyName,
            PolicyRule,
            CurValue,
            TriggerTime,
            Duration,
            RelatedResourceSet?.join('；') || '-'
          ]
        })
      )
      return acc
    }, [])
    if (productType !== 0) {
      headers.splice(4, 2)
      headers.pop()
      mapdata.forEach(item => {
        item.splice(4, 2)
        item.pop()
      })
    }
    if (mapdata?.length) {
      handleExportForm(headers, mapdata, `${productName}-${_$('正在告警资源')}`)
      Message.success(_$('下载成功'))
    } else {
      Message.error(_$('所选产品暂无数据'))
    }
  }

  // 告警抽屉scheme
  const alarmScheme = {
    PolicyName: {
      title: _$('告警策略名称'),
      width: 150,
      template(data, index) {
        const { PolicyId, PolicyName } = data
        return (
          <p
            className="table-rule-item"
            key={`${PolicyId}-${index}`}
            onClick={() => {
              window.open(`/#!/monitor/alarmServer/rule/detail/${PolicyId}`)
            }}
          >
            {PolicyName}
          </p>
        )
      }
    },
    PolicyRule: {
      title: _$('告警规则')
    },
    CurValue: {
      title: _$('触发值'),
      width: 80
    },
    TriggerTime: {
      title: _$('触发时间'),
      width: 100
    },
    Duration: {
      title: _$('持续时间'),
      width: 100
    }
  }

  return (
    <div className="alram-box" id={`alram-box-${productType}`}>
      <div className="header">
        <MonitorSearch
          placeholder={_$('输入实例ID、实例名称')}
          value={search}
          onChange={val => {
            setSearch(val)
          }}
        />
        <Icon
          size={20}
          className="ion-android-download"
          onClick={e => {
            exportData()
            e.stopPropagation()
          }}
        />
      </div>
      {scheme ? (
        <TablePage
          tableProps={{
            scheme,
            rowKey: item => item.InstanceId,
            data: filterData.slice(pageIndex * pageSize - pageSize, pageIndex * pageSize),
            rowCheckable: false,
            group: tableGroup,
            onChangeGroup: value => {
              setTableGroup(value)
            },
            checkedKeys: selectedKeys,
            onChangeCheckedKeys: (keys: string[]) => {
              setSelectedKeys(keys)
            }
          }}
          pageProps={{
            limits: [10, 20, 50, 100],
            total: filterData?.length,
            ...page,
            size: 'small',
            onChange: ({ value, limit }) => {
              setPage({
                pageIndex: value,
                pageSize: limit
              })
            }
          }}
        />
      ) : null}
      {productType === 0 ? (
        <Dialog
          title={`${_$('关联云硬盘')} - ${realtedData.InstanceName}`}
          value={realtedShow}
          draggable={false}
          onOk={() => {
            setRealtedShow(false)
            setRelatedData([])
          }}
          onChangeValue={value => {
            setRealtedShow(value)
          }}
        >
          <div>
            <Table
              checkType="none"
              data={realtedData?.RelatedResourceSet?.map(item => ({ id: item }))}
              rowKey={item => item.id}
            >
              <TableColumn key="id" title="云硬盘ID" />
            </Table>
          </div>
        </Dialog>
      ) : null}
      <Drawer
        title={`${_$('告警规则')} - ${alarmData.InstanceName}`}
        value={alarmShow}
        width={800}
        container={() => document.getElementById(`alram-box-${productType}`)}
        onOk={() => {
          setAlarmShow(false)
          setAlarmData([])
        }}
        onChangeValue={value => {
          if (value !== alarmShow) {
            setAlarmShow(value)
          }
        }}
      >
        <TablePage
          tableProps={{
            scheme: alarmScheme,
            rowKey: item => item.RuleId,
            data: alarmData?.Policies || [],
            checkType: 'none',
            onChangeGroup: value => {
              setTableGroup(value)
            },
            checkedKeys: selectedKeys,
            onChangeCheckedKeys: (keys: string[]) => {
              setSelectedKeys(keys)
            }
          }}
          showPage={false}
        />
      </Drawer>
    </div>
  )
}
export default AlarmingCollapseItem
